import * as THREE from './three'
import { Layers } from './Layers'
import { findClearPosition, isPositionClear } from './clearPlayerPosition'

/**
 * XR right thumbstick (user decision, 10 October 2026): left/right snap turn
 * 45°, forward shows the teleport arc and reticle (rotating the held stick
 * steers it left/right), release teleports, a
 * thumbstick click while the arc is shown cancels (Meta teleport rules).
 * The left thumbstick keeps free movement and is not handled here.
 * A stick that starts while its controller points at an interactive UI stays
 * with the UI, and once engaged it keeps its action until back to neutral.
 */

const STICK_ENGAGE = 0.6
const STICK_NEUTRAL = 0.25
export const XR_SNAP_ANGLE = 45

// Arc and target limits.
const ARC_SPEED = 9
const ARC_GRAVITY = 9.81
const ARC_TIME_STEP = 0.03
export const XR_ARC_SEGMENTS = 60
export const XR_TELEPORT_MAX_DISTANCE = 10
const MAX_SLOPE_COS = Math.cos(THREE.MathUtils.degToRad(30))
// Smily machines and their seats.
const EXCLUDED_TAGS = new Set(['course-station'])
// Clear spot searched around a reticle too close to an obstacle.
const NEARBY = { from: 0, to: 0.9, step: 0.3, angles: [0, 45, -45, 90, -90, 135, -135, 180] }

const UP = new THREE.Vector3(0, 1, 0)
const v1 = new THREE.Vector3()
const v2 = new THREE.Vector3()
const v3 = new THREE.Vector3()

export function stickDirection(x, z) {
  const ax = Math.abs(x)
  const az = Math.abs(z)
  if (Math.max(ax, az) < STICK_ENGAGE) return null
  if (az >= ax) return z < 0 ? 'up' : 'down'
  return x < 0 ? 'left' : 'right'
}

// While aiming, the stick angle steers the arc around the controller direction:
// up = where the controller points, up-left/up-right = arc turned that way.
export function steerArcDirection(direction, x, z) {
  return direction.applyAxisAngle(UP, -Math.atan2(x, -z))
}

function isNeutral(x, z) {
  return Math.hypot(x, z) < STICK_NEUTRAL
}

/**
 * Right thumbstick. `update` returns what the stick asks for this frame:
 * aiming, confirm (released while aiming), cancel and snap (-1 left / 1 right,
 * once per push as before).
 */
export class XRStickState {
  constructor() {
    this.state = null // null | 'aim' | 'turn' | 'blocked'
  }

  // Locked: ignore until the stick is released.
  block(x, z) {
    this.state = isNeutral(x, z) ? null : 'blocked'
  }

  update(x, z, click, { onUI = false } = {}) {
    const out = { aiming: false, confirm: false, cancel: false, snap: 0 }
    if (isNeutral(x, z)) {
      if (this.state === 'aim') out.confirm = true
      this.state = null
      return out
    }
    if (!this.state) {
      const dir = stickDirection(x, z)
      if (!dir) return out
      if (onUI) {
        this.state = 'blocked'
      } else if (dir === 'up') {
        this.state = 'aim'
      } else if (dir === 'left' || dir === 'right') {
        this.state = 'turn'
        out.snap = dir === 'left' ? -1 : 1
        return out
      } else {
        this.state = 'blocked'
      }
    }
    if (this.state === 'aim') {
      if (click) {
        this.state = 'blocked'
        out.cancel = true
      } else {
        out.aiming = true
      }
    }
    return out
  }
}

function copyHit(hit) {
  return { point: hit.point.clone(), normal: hit.normal.clone(), tag: hit.handle?.tag ?? null }
}

/**
 * Projectile arc from the controller. Fills `points` (Vector3 array of
 * XR_ARC_SEGMENTS + 1) and returns { count, hit } where hit is the first
 * environment/prop surface reached, or null.
 */
export function traceTeleportArc(physics, origin, direction, points) {
  const mask = Layers.environment.group | Layers.prop.group
  const velocity = v1.copy(direction).normalize().multiplyScalar(ARC_SPEED)
  const position = points[0].copy(origin)
  for (let i = 1; i <= XR_ARC_SEGMENTS; i++) {
    const next = points[i].copy(position).addScaledVector(velocity, ARC_TIME_STEP)
    next.y -= 0.5 * ARC_GRAVITY * ARC_TIME_STEP * ARC_TIME_STEP
    velocity.y -= ARC_GRAVITY * ARC_TIME_STEP
    const segment = v2.subVectors(next, position)
    const length = segment.length()
    const hit = length > 0 && physics.raycast(position, segment.divideScalar(length), length, mask)
    if (hit) {
      const result = copyHit(hit)
      next.copy(result.point)
      return { count: i + 1, hit: result }
    }
    position.copy(next)
  }
  return { count: XR_ARC_SEGMENTS + 1, hit: null }
}

/**
 * Landing for a reticle on a walkable floor (slope ≤ 30°, not a Smily station,
 * within 10 m horizontally, at any height reached by the arc).
 * The capsule must be clear: when the reticle is too close to an obstacle the
 * nearest clear spot within 0.9 m is used instead. Returns feet or null.
 */
export function validateTeleportTarget(physics, hit, feet) {
  if (!hit || hit.normal.y < MAX_SLOPE_COS || EXCLUDED_TAGS.has(hit.tag)) return null
  if (Math.hypot(hit.point.x - feet.x, hit.point.z - feet.z) > XR_TELEPORT_MAX_DISTANCE) return null
  const target = hit.point.clone()
  if (isPositionClear(physics, target)) return target
  const forward = v3.subVectors(hit.point, feet).setY(0)
  return findClearPosition(physics, target, forward, NEARBY)
}

const ARC_VALID_COLOR = '#ffffff'
const ARC_INVALID_COLOR = '#ff4545'

class TeleportArcVisual {
  constructor(scene, points) {
    this.points = points
    this.positions = new Float32Array((XR_ARC_SEGMENTS + 1) * 3)
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3))
    this.material = new THREE.LineBasicMaterial({ color: ARC_VALID_COLOR, transparent: true, opacity: 0.9, depthWrite: false })
    this.line = new THREE.Line(geometry, this.material)
    this.line.frustumCulled = false
    this.reticleMaterial = new THREE.MeshBasicMaterial({ color: ARC_VALID_COLOR, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide })
    this.reticle = new THREE.Mesh(new THREE.RingGeometry(0.22, 0.3, 32), this.reticleMaterial)
    this.reticle.rotation.x = -Math.PI / 2
    this.group = new THREE.Group()
    this.group.add(this.line, this.reticle)
    this.group.visible = false
    // Local-only helper outside the stage hit octree.
    scene.add(this.group)
  }

  show(count, target, hit) {
    for (let i = 0; i < count; i++) this.points[i].toArray(this.positions, i * 3)
    this.line.geometry.setDrawRange(0, count)
    this.line.geometry.attributes.position.needsUpdate = true
    const color = target ? ARC_VALID_COLOR : ARC_INVALID_COLOR
    this.material.color.set(color)
    this.reticleMaterial.color.set(color)
    // The reticle marks where the player will actually land.
    const at = target || hit?.point
    this.reticle.visible = !!at
    if (at) this.reticle.position.copy(at).y += 0.01
    this.group.visible = true
  }

  hide() {
    this.group.visible = false
  }

  destroy() {
    this.group.removeFromParent()
    this.line.geometry.dispose()
    this.material.dispose()
    this.reticle.geometry.dispose()
    this.reticleMaterial.dispose()
  }
}

/**
 * Drives the right thumbstick for the local player each frame.
 * Returns { turned } for PlayerLocal.update.
 */
export class XRLocomotion {
  constructor(player) {
    this.player = player
    this.world = player.world
    this.stick = new XRStickState()
    this.points = Array.from({ length: XR_ARC_SEGMENTS + 1 }, () => new THREE.Vector3())
    this.aiming = false
    this.target = null
    this.visual = null
  }

  update() {
    const player = this.player
    const control = player.control
    const stick = control.xrRightStick.value
    let turned = false
    if (player.locomotionLocks > 0) {
      this.endAim()
      this.stick.block(stick.x, stick.z)
    } else {
      const out = this.stick.update(stick.x, stick.z, control.xrRightStickBtn.pressed, {
        onUI: this.world.pointer?.isXRHandOnUI?.('Right'),
      })
      if (out.snap) {
        player.turnXRRigAtPlayer(-out.snap * XR_SNAP_ANGLE)
        turned = true
      }
      if (out.aiming) {
        this.aiming = true
        if (!this.aim(stick.x, stick.z)) {
          // Tracking lost: cancel instead of teleporting on release.
          this.stick.block(stick.x, stick.z)
          this.endAim()
        }
      } else if (this.aiming) {
        if (out.confirm && this.target) player.teleport({ position: this.target })
        this.endAim()
      }
    }
    // The right ray is suspended while its arc is shown; the left keeps interacting.
    this.world.pointer?.setXRSuspendedHand?.(this.aiming ? 'Right' : null)
    return { turned }
  }

  aim(x, z) {
    const ray = this.player.control.xrRightRayPose
    if (!ray?.valid || !ray.position) return false
    const physics = this.world.physics
    const dir = steerArcDirection(v3.set(0, 0, -1).applyQuaternion(ray.quaternion), x, z)
    const { count, hit } = traceTeleportArc(physics, ray.position, dir, this.points)
    this.target = validateTeleportTarget(physics, hit, this.player.base.position)
    if (!this.visual) this.visual = new TeleportArcVisual(this.world.stage.scene, this.points)
    this.visual.show(count, this.target, hit)
    return true
  }

  endAim() {
    this.aiming = false
    this.target = null
    this.visual?.hide()
  }

  reset() {
    this.endAim()
    this.stick.state = null
    this.world.pointer?.setXRSuspendedHand?.(null)
  }

  destroy() {
    this.reset()
    this.visual?.destroy()
    this.visual = null
  }
}
